import { Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { CmsConfig } from '@kion/libs/lmh/cms/src/lib/config/cms.config';
import { AuthsPartialState } from './auths.reducer';
import { AuthsQuery } from './auths.selectors';
import * as AuthsActions from './auths.actions';
import { ActionWithCorrelationAttributes } from '@kion/ngrx-helper';

@Injectable()
export class AuthsFacade {

  all$ = this.store.pipe(select(AuthsQuery.getAllAuths));
  allById$ = this.store.pipe(select(AuthsQuery.getAllAuthsById));
  searchResults$ = this.store.pipe(select(AuthsQuery.getSearchResults));
  isSearching$ = this.store.pipe(select(AuthsQuery.getIsSearching));
  lastCreatedId$ = this.store.pipe(select(AuthsQuery.getLastCreatedId));
  error$ = this.store.pipe(select(AuthsQuery.getError));
  inProcess = false;

  constructor(
	private store: Store<AuthsPartialState>,
	private baseSiteConfig: CmsConfig,
  ) { }

  protected dispatch(action, correlationId?: string): ActionWithCorrelationAttributes {
    if (correlationId) {
      action.__correlationId = correlationId;
    }
    this.store.dispatch(action);
    return action;
  }

  clientAuth(correlationId?: string): ActionWithCorrelationAttributes {
    return this.dispatch(
      AuthsActions.clientAuth(),
      correlationId
    );
  }

  userAuth(
    userName: string,
    accessToken: string,
    idToken: string,
    idp: string,
    oid: string,
    correlationId?: string
  ): ActionWithCorrelationAttributes {
    return this.dispatch(
      AuthsActions.userAuth({ accessToken: accessToken, idToken: idToken, idp: idp, oid: oid, userName: userName }),
      correlationId
    )
  }

  userAuthGlobal(
    userName: string,
    accessToken: string,
    idToken: string,
    idp: string,
    oid: string,
	unitNumber: string,
	hash: string,
	isGSMi: string,
	GSMiSite: string,
	dematicPunchOutKey: string,
	dematicPunchOutSid: string,
    correlationId?: string
  ): ActionWithCorrelationAttributes {
    return this.dispatch(
      AuthsActions.userAuthGlobal({ accessToken: accessToken, idToken: idToken, idp: idp, oid: oid, userName: userName, unitNumber: unitNumber, hash: hash, isGSMi: isGSMi, GSMiSite: GSMiSite, dematicPunchOutKey: dematicPunchOutKey, dematicPunchOutSid: dematicPunchOutSid}),
      correlationId
    )
  }

  checkWhoAmI(accessToken: string, correlationId?: string): ActionWithCorrelationAttributes {
    return this.dispatch(
      AuthsActions.checkWhoAmI({ accessToken: accessToken }),
      correlationId
    );
  }

  refreshToken(token: string, correlationId?: string): ActionWithCorrelationAttributes {
    return this.dispatch(
      AuthsActions.refreshUserToken({ token: token }),
      correlationId
    );
  }

  clearAuth(correlationId?: string): ActionWithCorrelationAttributes {
    return this.dispatch(
      AuthsActions.clearAuth(),
      correlationId
    );
  }

  deleteUserToken(correlationId?: string): ActionWithCorrelationAttributes {
    return this.dispatch(
        AuthsActions.deleteUserToken(),
        correlationId
    );
  }

  isUserLoggedIn(): boolean {
	let isLoggedIn = false;
	this.all$.subscribe(auth => {
		
      const userAuth = auth[0]?.user;
      const clientAuth = auth[0]?.client;
      let token = clientAuth?.accessToken;
      let hasClientToken = true;
      let isDematicUser = false;

      if (clientAuth) {
        if (new Date() >= new Date(clientAuth.expireDate)) {
          token = null;
          hasClientToken = false;
        }
      }
      if(userAuth) {
        token = userAuth.accessToken;
        isDematicUser = this.isDematicUser();
        if (userAuth) {
          if (new Date(userAuth.expireDate) <= new Date()) {
            token = null;
            if (hasClientToken) {
              token = clientAuth?.accessToken;
            }
          }
        }
      }
      if (token && isDematicUser) {
        isLoggedIn = true;
      }
    });
	return isLoggedIn;
  }

  getUserIdpLanguage(): string {
    if (this.isDematicUser()) {
      return null;
    }
    let idpLanguage = null;
    this.all$.subscribe(auth => {
      idpLanguage = auth[0]?.user.idpLanguage;
    });
    return idpLanguage;
  }

  private isDematicUser(): boolean {
    return this.baseSiteConfig.project === 'LR' || this.baseSiteConfig.project == 'Dematic';
  }
}
